/**
 * BenchmarkLeaderboardPanel — model benchmark rankings, HONESTLY labeled.
 *
 * Reads GET /api/benchmarks/leaderboard. These scores come from a
 * deterministic mock simulator (per-model bias profiles) unless a real
 * inference function is wired — the backend stamps `simulated` on every
 * entry and the panel labels simulated rankings loudly. A simulated ranking
 * presented as real model performance is a trust/diligence failure.
 */
import { FlaskConical, Trophy } from "lucide-react";

import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { useBenchmarkLeaderboard } from "@/sdk/hooks/use-benchmarks";

function fmtNum(value: unknown, digits: number): string {
  return typeof value === "number" && Number.isFinite(value)
    ? value.toFixed(digits)
    : "—";
}

function fmtPct(value: unknown): string {
  return typeof value === "number" && Number.isFinite(value)
    ? `${(value * 100).toFixed(0)}%`
    : "—";
}

function SimulatedBadge() {
  return (
    <span
      title="Scores are generated by a deterministic simulator (mock model bias profiles), not real model calls."
      className="inline-flex items-center gap-1 rounded-full border border-amber-500/50 bg-amber-500/10 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide text-amber-300"
    >
      <FlaskConical className="h-3 w-3" />
      Simulated
    </span>
  );
}

export function BenchmarkLeaderboardPanel() {
  const query = useBenchmarkLeaderboard();
  const leaderboard = query.data?.leaderboard ?? [];
  const simulated = query.data?.simulated ?? true;

  return (
    <Panel
      title="Model Benchmarks"
      description="Scenario-suite rankings by composite score (lower = better)"
      actions={simulated ? <SimulatedBadge /> : null}
    >
      {query.isError ? (
        <QueryErrorState
          what="benchmark leaderboard"
          onRetry={() => void query.refetch()}
        />
      ) : query.isLoading ? (
        <LoadingState text="Loading benchmarks..." />
      ) : leaderboard.length === 0 ? (
        <div className="py-6 text-center">
          <Trophy className="mx-auto h-8 w-8 text-white/20" />
          <p className="mt-2 text-sm text-white/40">No benchmark runs recorded.</p>
          <p className="text-xs text-white/25">
            Runs are triggered via POST /api/benchmarks/run (admin). Scores are
            simulated until real inference is wired in.
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          {simulated ? (
            <p className="mb-2 text-[0.7rem] text-amber-300/80">
              {query.data?.note ??
                "Scores below are simulator output, not real model calls."}
            </p>
          ) : null}
          {leaderboard.map((entry) => (
            <div
              key={entry.model_key}
              className="flex items-center justify-between gap-2 rounded border border-white/5 px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="w-8 shrink-0 font-mono text-xs text-white/40">
                  #{entry.rank}
                </span>
                <span className="truncate text-xs font-medium text-white/80">
                  {entry.model_key}
                </span>
                {entry.simulated !== false ? (
                  <span className="rounded bg-amber-500/15 px-1 py-0.5 text-[0.6rem] font-semibold text-amber-300/80">
                    sim
                  </span>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-3 font-mono text-[0.7rem] text-white/50">
                {/* Legacy/corrupt stored rows may miss numeric fields — the
                    store only backfills provenance, not metrics. Guard every
                    number so one bad row can't crash the page (Qodo, PR #1892). */}
                <span>composite {fmtNum(entry.composite_score, 3)}</span>
                <span>Brier {fmtNum(entry.mean_brier_score, 3)}</span>
                <span>dir {fmtPct(entry.direction_accuracy)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
